import { useEffect, useRef } from "react";
import { prepareWorkbook, readWorkbookStatus, readWorkbookChunk } from "../api/workbookDownloads";

const pause = (signal: AbortSignal, milliseconds = 3000) => new Promise<void>((resolve, reject) => {
    const abort = () => { window.clearTimeout(timer); signal.removeEventListener("abort", abort); reject(new DOMException("Download cancelled", "AbortError")); };
    const timer = window.setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, milliseconds);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
});

const retryRequest = async <T,>(request: () => Promise<T>, signal: AbortSignal): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
        try { return await request(); }
        catch (error) {
            const failure = error as { response?: { status: number }; code?: string; name?: string };
            if (signal.aborted || failure.code === "ERR_CANCELED" || failure.name === "AbortError" || attempt >= 2
                || (failure.response && failure.response.status < 500)) throw error;
            await pause(signal);
        }
    }
};

export type WorkbookDownloadStage = "submitting" | "queued" | "running" | "ready" | "downloading";
export interface WorkbookDownloadProgress { stage: WorkbookDownloadStage; percent?: number; }
export interface DownloadHistoryEntry {
    id: number;
    label: string;
    reportId: number;
    configVersionId: number | null;
    scopeLabel?: string;
    stage: WorkbookDownloadStage | "completed" | "failed" | "cancelled";
    percent?: number;
    startedAt: number;
    finishedAt?: number;
    error?: string;
}

// Preparation/polling never holds a long HTTP request; ready files use bounded range requests.
export const useWorkbookDownload = (configurationLoading = false) => {
    const loadingConfiguration = useRef(configurationLoading);
    loadingConfiguration.current = configurationLoading;
    const active = useRef(new Set<AbortController>());
    const transfers = useRef<Promise<void>>(Promise.resolve());
    useEffect(() => () => {
        active.current.forEach(controller => controller.abort());
        active.current.clear();
    }, []);
    return async (reportId: number, versionId: number | null | undefined, onPhase: (phase: string) => void, onProgress?: (progress: WorkbookDownloadProgress) => void) => {
        const controller = new AbortController();
        active.current.add(controller);
        const signal = controller.signal;
        const yieldToConfiguration = async () => {
            while (loadingConfiguration.current) await pause(signal, 250);
        };
        let releaseTransfer: (() => void) | undefined;
        try {
            onProgress?.({ stage: "submitting" });
            let job = await retryRequest(() => prepareWorkbook(reportId, versionId, signal), signal);
            if (!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(job.jobId))
                throw new Error("The report download response is invalid.");
            const id = job.jobId;
            const deadline = Date.now() + 60 * 60 * 1000;
            let lastPhase = "";
            let statusChecks = 0;
            while (job.state !== "ready") {
                if (job.state === "failed") throw new Error(job.error || "The report could not be prepared.");
                if (job.state !== "queued" && job.state !== "running") throw new Error("The report download response is invalid.");
                const phase = job.state === "queued" ? "Report queued..." : "Preparing Excel workbook...";
                if (phase !== lastPhase) { onPhase(phase); onProgress?.({ stage: job.state }); lastPhase = phase; }
                if (Date.now() >= deadline) throw new Error("Report preparation timed out. Please retry.");
                // Keep waiting jobs from occupying the browser/SQL request budget.
                // The first check stays quick; subsequent queued checks are less frequent.
                await pause(signal, statusChecks++ === 0 ? 2000 : job.state === "queued" ? 8000 : 4000);
                await yieldToConfiguration();
                job = await retryRequest(() => readWorkbookStatus(id, signal), signal);
            }
            if (!Number.isSafeInteger(job.fileBytes) || job.fileBytes <= 0) throw new Error("The prepared report is empty or invalid.");
            // Queue ready file transfers too, so several large blobs are not built together.
            const previousTransfer = transfers.current;
            transfers.current = new Promise<void>(resolve => {
                // Even a failed waiting job must retain the order of earlier transfers.
                releaseTransfer = () => { void previousTransfer.then(resolve); };
            });
            onProgress?.({ stage: "ready" });
            let transferAvailable = false;
            void previousTransfer.then(() => { transferAvailable = true; });
            while (!transferAvailable) {
                const heartbeat = new AbortController();
                const abort = () => heartbeat.abort();
                signal.addEventListener("abort", abort, { once: true });
                if (signal.aborted) heartbeat.abort();
                try { await Promise.race([previousTransfer, pause(heartbeat.signal, 60000)]); }
                finally { signal.removeEventListener("abort", abort); heartbeat.abort(); }
                if (!transferAvailable) {
                    // Renew ready-file expiry while another browser transfer occupies the slot.
                    const status = await retryRequest(() => readWorkbookStatus(id, signal), signal);
                    if (status.state !== "ready") throw new Error(status.error || "This download is no longer available. Please retry.");
                }
            }
            if (signal.aborted) throw new DOMException("Download cancelled", "AbortError");
            onProgress?.({ stage: "downloading", percent: 0 });
            const parts: Blob[] = [];
            const chunkBytes = 4 * 1024 * 1024;
            let headers: Record<string, any> = {};
            for (let start = 0; start < job.fileBytes; start += chunkBytes) {
                const end = Math.min(job.fileBytes - 1, start + chunkBytes - 1);
                await yieldToConfiguration();
                const response = await retryRequest(() => readWorkbookChunk(id, start, end, signal), signal);
                const range = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(response.headers["content-range"] || "");
                if (response.status !== 206 || !range || Number(range[1]) !== start || Number(range[2]) !== end
                    || Number(range[3]) !== job.fileBytes || response.data.size !== end - start + 1)
                    throw new Error("The report download was incomplete. Please retry.");
                headers = response.headers;
                parts.push(response.data as Blob);
                const percent = Math.floor((end + 1) / job.fileBytes * 100);
                onPhase(`Downloading workbook... ${percent}%`);
                onProgress?.({ stage: "downloading", percent });
            }
            return { data: new Blob(parts, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), headers };
        } finally {
            releaseTransfer?.();
            active.current.delete(controller);
        }
    };
};
